import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@material-ui/core/styles'
import { useDispatch, useSelector } from 'react-redux'

import {
  Tabs,
  Tab,
  Card,
  Divider,
  CircularProgress,
  Badge,
} from '@material-ui/core'

import Clock from '../../atoms/display/clock'
import IconButton from '../../atoms/inputs/iconButton'
import useStyles from './ticketListStyle'
import RenderAccordionParent from '../../molecules/ticket/accordionMasterComponent'
import RenderAccordionDetails from '../../molecules/ticket/accordionDetails'
import RenderHorizontalStepper from '../../molecules/ticket/stepperHorizontal'

import Typography from '../../atoms/display/typography'
import messages from '../../../services/messages'

import { useSnackbar } from 'notistack'

import {
  FolderShared as TicketIcon,
  RefreshRounded as LaunchIcon,
  ArrowBack,
  FormatListBulletedRounded as Bookmark,
} from '@material-ui/icons'

import {
  getConsumerTickets,
  apiError,
  setConsumerTickets,
} from '../../../redux/consumerTickets/actions'

import Tooltip from '../../atoms/display/tooltip'

import api from '../../../services/api'

const Ticket = ({
  small = false,
  consumerId,
  channel,
  openTicketsContent,
  setOpenTicketsContent,
}) => {
  const size = useSelector((state) => state.preferences)
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  let useStylesParams = {
    size: size,
  }
  const classes = useStyles(useStylesParams)

  const theme = useTheme()
  const dispatch = useDispatch()

  const activeConversation = useSelector((state) => state.activeConversation)
  const conversation = useSelector(
    (state) => state.conversations.conversations[activeConversation]
  )

  const consumerTicketsData = useSelector(
    (state) => state.consumerTickets.data?.[consumerId]
  )
  const consumerTicketsStatus = useSelector(
    (state) => state.consumerTickets.status?.[consumerId]
  )

  const [tabValue, setTabValue] = useState(0)
  const [scrollToBottom, setScrollToBottom] = useState(false)

  useEffect(() => {
    scrollToBottom && setScrollToBottom(false)
  }, [scrollToBottom])

  const handleRefresh = async () => {
    dispatch(getConsumerTickets(consumerId))
    try {
      const response = await messages.get(
        `/tech4h-messages/consumer/${consumerId}/tickets`
      )
      if (response.status === 200) {
        dispatch(setConsumerTickets(consumerId, response.data.data))
      } else {
        throw response
      }
    } catch (error) {
      let message = t('alerts.reloadWidgetInfo')
      dispatch(apiError(consumerId, message))
    }
  }

  const renderHeaderIcons = () => {
    return (
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            flexWrap: 'nowrap',
          }}
        >
          <IconButton color="primary">
            <TicketIcon style={{ color: theme.palette.primary.main }} />
          </IconButton>
          {openTicketsContent ? (
            <h3>{t('commons.in_queue')}</h3>
          ) : (
            <h3>{t('commons.ticket')}</h3>
          )}
        </div>
      </>
    )
  }

  const renderHeader = () => {
    if (size === 'small') {
      return (
        <>
          {renderHeaderIcons()}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexWrap: 'nowrap',
            }}
          >
            <div style={{ width: 10 }} />
            <div
              style={{
                visibility: openTicketsContent ? 'hidden' : 'visible',
              }}
            >
              <Clock
                chatId={activeConversation}
                endTime={conversation?.end}
                assignedTime={conversation?.assignedTime}
                ticket
                withSummary
              />
            </div>
          </div>
          <div style={{ width: 10 }} />
          <IconButton
            color="primary"
            role={'button'}
            onClick={() => setOpenTicketsContent(!openTicketsContent)}
            style={{
              cursor: 'pointer',
            }}
          >
            {openTicketsContent ? (
              <ArrowBack />
            ) : (
              <Tooltip placement="bottom" title={t('tickets.in_queue')}>
                <div>
                  <Badge
                    badgeContent={
                      consumerTicketsStatus?.loading
                        ? null
                        : consumerTicketsData?.count ?? '-'
                    }
                    color="primary"
                  >
                    <Bookmark style={{ color: theme.palette.primary.main }} />
                  </Badge>
                </div>
              </Tooltip>
            )}
          </IconButton>
        </>
      )
    } else {
      return (
        <>
          {renderHeaderIcons()}
          <IconButton
            color="primary"
            role={'button'}
            onClick={() => setOpenTicketsContent(!openTicketsContent)}
            style={{
              cursor: 'pointer',
            }}
          >
            {' '}
            {openTicketsContent ? (
              <ArrowBack />
            ) : (
              <Typography variant="subtitle2">
                {`${t('tickets.in_queue')} (${
                  consumerTicketsData?.count ?? '-'
                })`}
              </Typography>
            )}
          </IconButton>
          <div
            style={{
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexWrap: 'nowrap',
              display: openTicketsContent ? 'none' : 'flex',
            }}
          >
            <Clock
              chatId={activeConversation}
              endTime={conversation?.end}
              assignedTime={conversation?.assignedTime}
              ticket
            />
          </div>
        </>
      )
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const TicketsInQueue = useCallback(
    () => (
      <div style={{ marginTop: 10 }}>
        <div className={classes.content}>
          <div>
            <Typography variant="body1">{t('Total') + ':'}</Typography>
            <Typography variant="h6">
              {` ${consumerTicketsData?.count ?? '-'}`}
            </Typography>
          </div>
          <div>
            <Tooltip placement="bottom" title={t('commons.refresh')}>
              <div>
                <IconButton color="primary" onClick={() => handleRefresh()}>
                  {consumerTicketsStatus?.loading ? (
                    <CircularProgress
                      className={classes.headerIcon}
                      size="22px"
                    />
                  ) : (
                    <LaunchIcon className={classes.headerIcon} />
                  )}
                </IconButton>
              </div>
            </Tooltip>
          </div>
        </div>
        <Divider className={classes.divider} />
        {consumerTicketsData?.tickets?.length ? (
          <div className={classes.card_container}>
            {consumerTicketsData.tickets.map((ticket, index) => (
              <Card className={classes.card}>
                <div key={index}>
                  <RenderAccordionDetails
                    history={ticket?.processData}
                    conversation={conversation}
                    invisibleSla
                  />
                  <RenderHorizontalStepper
                    history={ticket?.processData}
                    horizontalValue={1}
                  />
                </div>
              </Card>
            ))}
          </div>
        ) : null}
        {!consumerTicketsData?.tickets?.length &&
          !consumerTicketsStatus?.loading && (
            <div className={classes.content}>
              {consumerTicketsStatus?.error ? (
                <Typography variant="h6">
                  {t(consumerTicketsStatus.error)}
                </Typography>
              ) : (
                <Typography variant="h6">{t('tickets.not_found')}</Typography>
              )}
            </div>
          )}
        {!consumerTicketsData?.tickets?.length &&
          consumerTicketsStatus?.loading && (
            <div className={classes.content}>
              <CircularProgress
                style={{
                  margin: 'auto',
                }}
              />
            </div>
          )}
      </div>
    ),
    [
      consumerTicketsData,
      consumerTicketsStatus,
      classes,
      conversation,
      consumerId,
    ]
  )

  useEffect(() => {
    if (consumerTicketsData) return
    handleRefresh()
  }, [consumerId])

  useEffect(() => {
    if (consumerTicketsStatus?.error) {
      enqueueSnackbar(consumerTicketsStatus?.error, {
        variant: 'error',
      })
    }
  }, [consumerTicketsStatus?.error])

  return (
    <>
      <div className={classes.root}>
        <div
          className={classes.header}
          style={{
            marginBottom: 8,
          }}
        >
          {renderHeader()}
        </div>

        {openTicketsContent ? (
          <TicketsInQueue />
        ) : (
          <>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              variant="fullWidth"
              textColor="primary"
              style={{ marginLeft: 15, marginRight: 15 }}
            >
              <Tab label={t('commons.recentData')} />
              <Tab label={t('commons.serviceHistory')} />
            </Tabs>
            {tabValue === 0 ? (
              <div>
                <RenderAccordionParent
                  parent={true}
                  small={small}
                  conversation={conversation}
                />
              </div>
            ) : (
              <div>
                <RenderAccordionParent
                  parent={false}
                  small={small}
                  conversation={conversation}
                  historic={true}
                  consumerId={consumerId}
                  channel={channel}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

export default React.memo(Ticket)
